import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, ExternalLink, KeyRound, Lightbulb, BookOpen, Search } from "lucide-react";
import { Cloud, Shield, ShieldCheck, Crosshair, Bird, Workflow, Radar, Activity, Database, Dog, Bug } from "lucide-react";
import { useT } from "@/i18n";

const ICONS = {
  Cloud, Search, Shield, ShieldCheck, Crosshair, Bird, Workflow, Radar, Activity, Database, Dog, Bug,
};

const FAQ = [
  {
    q: { en: "Do I need to share my password with Lumioo?", tr: "Şifremi Lumioo ile paylaşmam gerekiyor mu?" },
    a: {
      en: "For most platforms NO — you generate a scoped API token and paste only the token. Salesforce + Wazuh are exceptions: they need username+password+security token because their APIs don't offer pure token-based auth. Even then, credentials are stored only on your account inside Lumioo.",
      tr: "Çoğu platform için HAYIR — sen kendi panelinde sınırlı izinli bir API token üretirsin, sadece o token'ı yapıştırırsın. Salesforce ve Wazuh istisna: bunların API'leri username+password+security token istiyor. Tüm credential'lar yalnızca senin Lumioo hesabında saklanır.",
    },
  },
  {
    q: { en: "Why are some platforms 'Coming soon'?", tr: "Bazı platformlar neden 'Coming soon'?" },
    a: {
      en: "We've implemented REAL API connections for Salesforce, Splunk, and Sentry. The remaining 9 platforms (Defender, Trend Micro, SentinelOne, CrowdStrike, Cortex XSOAR, Wazuh, QRadar, Elastic, Datadog) have full UI + token storage + mock case data right now. When you provide a working token, we activate the real API in minutes.",
      tr: "Salesforce, Splunk ve Sentry için GERÇEK API bağlantısı kurduk. Diğer 9 platform şu an UI + token saklama + sahte case verisi sunuyor. Sen geçerli bir token sağladığında, gerçek API'yi dakikalar içinde aktive ediyoruz.",
    },
  },
  {
    q: { en: "I don't have an account on Platform X — can I still use Lumioo for it?", tr: "Platform X'te hesabım yok — yine de kullanabilir miyim?" },
    a: {
      en: "No. Lumioo fetches cases from YOUR account on each platform. If you have no account, no cases to fetch. Use the platform's free trial / community edition (e.g. Salesforce Developer Edition is free forever) to test.",
      tr: "Hayır. Lumioo senin hesabındaki case'leri çeker — hesabın yoksa çekecek bir şey yok. Platformun ücretsiz deneme veya açık kaynak sürümünü kullan (örn. Salesforce Developer Edition sınırsız ücretsiz).",
    },
  },
  {
    q: { en: "What if my token gets compromised?", tr: "Token'ım çalınırsa?" },
    a: {
      en: "Revoke the token in the platform's settings (each platform has a 'Revoke / Delete token' option). Then in Lumioo, click 'Disconnect' on the platform card and reconnect with a new token.",
      tr: "İlgili platformun ayarlarından token'ı iptal et (her platformda 'Revoke / Delete token' var). Sonra Lumioo'da kartın 'Disconnect' butonuna bas, yeni token ile bağlan.",
    },
  },
  {
    q: { en: "Are my tokens encrypted at rest?", tr: "Token'larım depolamada şifreli mi?" },
    a: {
      en: "Not yet — they're stored in MongoDB in plaintext today. We're adding Fernet encryption in the next iteration. If you're storing production keys, generate read-only / least-privilege tokens until then.",
      tr: "Şu an değil — MongoDB'de düz metin saklanıyor. Bir sonraki iterasyonda Fernet şifreleme ekliyoruz. Üretim key'i kullanacaksan o zamana kadar yalnız-okuma / minimum yetkili token kullan.",
    },
  },
];

function PlatformGuide({ p, lang }) {
  const Icon = ICONS[p.icon] || Shield;
  return (
    <div className="border border-zinc-200 bg-white p-6" id={`platform-${p.platform}`} data-testid={`help-${p.platform}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: p.color + "1A" }}>
          <Icon className="w-5 h-5" style={{ color: p.color }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-bold tracking-tight">{p.name}</h3>
            {p.implemented ? (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded">Real API</span>
            ) : (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">Mock cases</span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{p.category}</div>
        </div>
      </div>

      {p.direct_link && (
        <a
          href={p.direct_link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {p.direct_link_label || `Open ${p.name}`}
        </a>
      )}

      <div className="mt-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1.5">{lang === "tr" ? "Adım adım" : "Step by step"}</div>
        <ol className="list-decimal list-inside space-y-1.5 text-sm text-zinc-700">
          {p.guide.map((g, i) => (<li key={i} className="leading-relaxed">{g}</li>))}
        </ol>
      </div>

      {p.token_format && (
        <div className="mt-4 flex items-start gap-2 text-xs bg-zinc-50 border border-zinc-200 rounded p-2.5">
          <KeyRound className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-zinc-700 uppercase text-[10px] tracking-wider">{lang === "tr" ? "Token biçimi" : "Token format"}</div>
            <div className="text-zinc-600 mt-0.5 font-mono break-all">{p.token_format}</div>
          </div>
        </div>
      )}

      {p.tips && p.tips.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 mb-1.5 flex items-center gap-1">
            <Lightbulb className="w-3 h-3" /> {lang === "tr" ? "İpuçları" : "Pro tips"}
          </div>
          <ul className="space-y-1 text-xs text-zinc-700">
            {p.tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-amber-600 shrink-0">•</span>
                <span className="leading-relaxed">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {p.fields && p.fields.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1.5">{lang === "tr" ? "İstenen bilgiler" : "What Lumioo asks for"}</div>
          <ul className="text-xs text-zinc-700 space-y-0.5 font-mono">
            {p.fields.map((f) => (<li key={f.key}>· <span className="font-bold">{f.key}</span> — {f.label}</li>))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const { t, lang } = useT();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/integrations")
      .then((r) => setPlatforms(r.data))
      .finally(() => setLoading(false));
  }, []);

  const visible = platforms.filter((p) =>
    !filter || p.name.toLowerCase().includes(filter.toLowerCase()) || p.category.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="help-page">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("help_eyebrow")}</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{t("help_h1")}</h1>
      <p className="mt-2 text-zinc-600 max-w-2xl">{t("help_sub")}</p>

      <div className="mt-8 relative max-w-lg">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input
          data-testid="help-search"
          type="text"
          placeholder={lang === "tr" ? "Platform veya kategori ara…" : "Search platform or category…"}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-zinc-200 bg-white focus:outline-none focus:border-blue-600 rounded-md"
        />
      </div>

      {loading ? (
        <div className="mt-10 p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      ) : (
        <>
          {/* Quick jump */}
          <div className="mt-8 flex flex-wrap gap-1.5">
            {visible.map((p) => (
              <a
                key={p.platform}
                href={`#platform-${p.platform}`}
                className="text-xs font-semibold px-2.5 py-1 rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-800"
              >
                {p.name}
              </a>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {visible.map((p) => <PlatformGuide key={p.platform} p={p} lang={lang} />)}
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <div className="flex items-center gap-2 mb-6">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <h2 className="text-2xl font-bold tracking-tight">{t("help_faq")}</h2>
            </div>
            <div className="space-y-3" data-testid="help-faq">
              {FAQ.map((item, i) => (
                <details key={i} className="border border-zinc-200 bg-white p-4 group">
                  <summary className="cursor-pointer font-semibold text-zinc-900 list-none flex items-center justify-between gap-2">
                    <span>{item.q[lang]}</span>
                    <span className="text-blue-600 transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{item.a[lang]}</p>
                </details>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
